import * as z from 'zod';

export const CreateTriggerRequest = (() => {
  let wixAutomationsTriggercatalogV1FilterSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        _id: z
          .string()
          .describe('Filter ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        fieldKeyType: z
          .enum([
            'UNKNOWN_FIELD_KEY_TYPE',
            'STATIC_FIELD_KEY',
            'DYNAMIC_FIELD_KEY',
          ])
          .optional(),
        valueInput: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNKNOWN_VALUE_INPUT',
                  'ENTITY_SELECTOR',
                  'USER_INPUT',
                  'STATIC_ITEMS',
                ])
                .optional(),
              label: z.string().describe('Input field label.').optional(),
              description: z
                .string()
                .describe('Input field description. May be used for a tooltip.')
                .optional()
                .nullable(),
              required: z
                .boolean()
                .describe(
                  'Whether the Wix user is required to enter a value in the field.\n\nDefault: `false`'
                )
                .optional(),
              reevaluateDynamicSchema: z
                .boolean()
                .describe(
                  'Whether Wix should reevaluate the dynamic schema when the field value changes.\n\nDefault: `false`'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
              }),
              z.object({
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
                entitySelector: z
                  .object({
                    _id: z.string().describe('Entity selector ID.').optional(),
                    queryFieldToFilterIdMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                      )
                      .optional(),
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryFieldToValueMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                      )
                      .optional(),
                  })
                  .describe('Entity selector details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                staticItems: z.never().optional(),
                userInput: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['UNKNOWN_USER_INPUT', 'NUMBER', 'BOOLEAN'])
                        .describe('User input type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z.never().optional(),
                      }),
                      z.object({
                        booleanInputOptions: z.never().optional(),
                        numberInputOptions: z
                          .object({
                            defaultValue: z
                              .number()
                              .int()
                              .describe('Default number value.')
                              .optional()
                              .nullable(),
                            minValue: z
                              .number()
                              .int()
                              .describe('Minimum number value.')
                              .optional()
                              .nullable(),
                            maxValue: z
                              .number()
                              .int()
                              .describe('Maximum number value.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Options for user input of type `number`.'),
                      }),
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z
                          .object({
                            defaultValue: z
                              .boolean()
                              .describe('Default boolean value.')
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for user input of type `boolean`.'
                          ),
                      }),
                    ])
                  )
                  .describe('User input details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z
                  .object({
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether the Wix user can select multiple items\n\nDefault: `false`.'
                      )
                      .optional(),
                    staticValues: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('item identifier')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          value: z.string().describe('static value').optional(),
                          displayName: z
                            .string()
                            .describe('optional displayName for the value')
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Static value details.'),
              }),
            ])
          )
          .describe('Input field metadata.')
          .optional(),
        followUpFilters: z
          .array(z.lazy(() => wixAutomationsTriggercatalogV1FilterSchema))
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z.never().optional(),
        }),
        z.object({
          dynamicFieldKey: z.never().optional(),
          staticFieldKey: z.string().describe('The static field key.'),
        }),
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z
            .object({
              label: z.string().describe('Field key label.').optional(),
              description: z
                .string()
                .describe('Field key description.')
                .optional()
                .nullable(),
              entitySelector: z
                .object({
                  _id: z.string().describe('Entity selector ID.').optional(),
                  queryFieldToFilterIdMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                    )
                    .optional(),
                  multiSelect: z
                    .boolean()
                    .describe(
                      'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                    )
                    .optional(),
                  queryFieldToValueMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                    )
                    .optional(),
                })
                .describe(
                  'Selects the entity to be used as a dynamic field key.'
                )
                .optional(),
            })
            .describe('Dynamic field key data.'),
        }),
      ])
    );
  return z.object({
    options: z
      .object({
        trigger: z
          .object({
            _id: z
              .string()
              .describe('Trigger ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe(
                'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            triggerKey: z
              .string()
              .describe(
                'Trigger key.\n\nLearn about [setting up a trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).'
              )
              .min(1)
              .max(100),
            payloadDataSchema: z
              .record(z.string(), z.any())
              .describe(
                'Trigger [payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).'
              ),
            displayName: z
              .string()
              .describe(
                'Trigger display name. Users see this name when they [choose a trigger for an automation](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger).'
              )
              .min(5)
              .max(80),
            filters: z
              .array(wixAutomationsTriggercatalogV1FilterSchema)
              .max(5)
              .optional(),
            implementedMethods: z
              .object({
                validateConfiguration: z
                  .boolean()
                  .describe(
                    'Whether [Validate Configuration](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/validate-configuration) is implemented.\n\nDefault: `false`.'
                  )
                  .optional(),
                getDynamicSchema: z
                  .boolean()
                  .describe(
                    'Whether [Get Dynamic Schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/get-dynamic-schema) is implemented.\n\nDefault: `false`.'
                  )
                  .optional(),
                refreshPayload: z
                  .boolean()
                  .describe(
                    'Whether [Refresh Payload](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/refresh-payload) is implemented.\n\nDefault: `false`.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional [service plugin methods](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction#implement-the-service-plugin-methods) implemented for this trigger.'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the trigger was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the trigger was last updated.')
              .optional()
              .nullable(),
            baseUri: z
              .string()
              .describe(
                'Trigger base URI. Wix calls your trigger at this base address.\n\nLearn more about the [Wix Automations Trigger Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction).'
              )
              .max(256)
              .optional()
              .nullable(),
          })
          .describe('Trigger to add to the app.')
          .optional(),
      })
      .optional(),
  });
})();
export const CreateTriggerResponse = (() => {
  let wixAutomationsTriggercatalogV1FilterSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        _id: z
          .string()
          .describe('Filter ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        fieldKeyType: z
          .enum([
            'UNKNOWN_FIELD_KEY_TYPE',
            'STATIC_FIELD_KEY',
            'DYNAMIC_FIELD_KEY',
          ])
          .describe('Field key type.')
          .optional(),
        valueInput: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNKNOWN_VALUE_INPUT',
                  'ENTITY_SELECTOR',
                  'USER_INPUT',
                  'STATIC_ITEMS',
                ])
                .describe('Input value type.')
                .optional(),
              label: z.string().describe('Input field label.').optional(),
              description: z
                .string()
                .describe('Input field description. May be used for a tooltip.')
                .optional()
                .nullable(),
              required: z
                .boolean()
                .describe(
                  'Whether the Wix user is required to enter a value in the field.\n\nDefault: `false`'
                )
                .optional(),
              reevaluateDynamicSchema: z
                .boolean()
                .describe(
                  'Whether Wix should reevaluate the dynamic schema when the field value changes.\n\nDefault: `false`'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
              }),
              z.object({
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
                entitySelector: z
                  .object({
                    _id: z.string().describe('Entity selector ID.').optional(),
                    queryFieldToFilterIdMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                      )
                      .optional(),
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryFieldToValueMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                      )
                      .optional(),
                  })
                  .describe('Entity selector details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                staticItems: z.never().optional(),
                userInput: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['UNKNOWN_USER_INPUT', 'NUMBER', 'BOOLEAN'])
                        .describe('User input type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z.never().optional(),
                      }),
                      z.object({
                        booleanInputOptions: z.never().optional(),
                        numberInputOptions: z
                          .object({
                            defaultValue: z
                              .number()
                              .int()
                              .describe('Default number value.')
                              .optional()
                              .nullable(),
                            minValue: z
                              .number()
                              .int()
                              .describe('Minimum number value.')
                              .optional()
                              .nullable(),
                            maxValue: z
                              .number()
                              .int()
                              .describe('Maximum number value.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Options for user input of type `number`.'),
                      }),
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z
                          .object({
                            defaultValue: z
                              .boolean()
                              .describe('Default boolean value.')
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for user input of type `boolean`.'
                          ),
                      }),
                    ])
                  )
                  .describe('User input details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z
                  .object({
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether the Wix user can select multiple items\n\nDefault: `false`.'
                      )
                      .optional(),
                    staticValues: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('item identifier')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          value: z.string().describe('static value').optional(),
                          displayName: z
                            .string()
                            .describe('optional displayName for the value')
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Static value details.'),
              }),
            ])
          )
          .describe('Input field metadata.')
          .optional(),
        followUpFilters: z
          .array(z.lazy(() => wixAutomationsTriggercatalogV1FilterSchema))
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z.never().optional(),
        }),
        z.object({
          dynamicFieldKey: z.never().optional(),
          staticFieldKey: z.string().describe('The static field key.'),
        }),
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z
            .object({
              label: z.string().describe('Field key label.').optional(),
              description: z
                .string()
                .describe('Field key description.')
                .optional()
                .nullable(),
              entitySelector: z
                .object({
                  _id: z.string().describe('Entity selector ID.').optional(),
                  queryFieldToFilterIdMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                    )
                    .optional(),
                  multiSelect: z
                    .boolean()
                    .describe(
                      'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                    )
                    .optional(),
                  queryFieldToValueMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                    )
                    .optional(),
                })
                .describe(
                  'Selects the entity to be used as a dynamic field key.'
                )
                .optional(),
            })
            .describe('Dynamic field key data.'),
        }),
      ])
    );
  return z.object({
    _id: z
      .string()
      .describe('Trigger ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    appId: z
      .string()
      .describe(
        'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    triggerKey: z
      .string()
      .describe(
        'Trigger key.\n\nLearn about [setting up a trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).'
      )
      .min(1)
      .max(100)
      .optional(),
    payloadDataSchema: z
      .record(z.string(), z.any())
      .describe(
        'Trigger [payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).'
      )
      .optional()
      .nullable(),
    displayName: z
      .string()
      .describe(
        'Trigger display name. Users see this name when they [choose a trigger for an automation](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger).'
      )
      .min(5)
      .max(80)
      .optional(),
    filters: z
      .array(wixAutomationsTriggercatalogV1FilterSchema)
      .max(5)
      .optional(),
    implementedMethods: z
      .object({
        validateConfiguration: z
          .boolean()
          .describe(
            'Whether [Validate Configuration](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/validate-configuration) is implemented.\n\nDefault: `false`.'
          )
          .optional(),
        getDynamicSchema: z
          .boolean()
          .describe(
            'Whether [Get Dynamic Schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/get-dynamic-schema) is implemented.\n\nDefault: `false`.'
          )
          .optional(),
        refreshPayload: z
          .boolean()
          .describe(
            'Whether [Refresh Payload](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/refresh-payload) is implemented.\n\nDefault: `false`.'
          )
          .optional()
          .nullable(),
      })
      .describe(
        'Additional [service plugin methods](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction#implement-the-service-plugin-methods) implemented for this trigger.'
      )
      .optional(),
    _createdDate: z
      .date()
      .describe('Date and time the trigger was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the trigger was last updated.')
      .optional()
      .nullable(),
    baseUri: z
      .string()
      .describe(
        'Trigger base URI. Wix calls your trigger at this base address.\n\nLearn more about the [Wix Automations Trigger Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction).'
      )
      .max(256)
      .optional()
      .nullable(),
  });
})();
export const UpdateTriggerRequest = (() => {
  let wixAutomationsTriggercatalogV1FilterSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        _id: z
          .string()
          .describe('Filter ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        fieldKeyType: z
          .enum([
            'UNKNOWN_FIELD_KEY_TYPE',
            'STATIC_FIELD_KEY',
            'DYNAMIC_FIELD_KEY',
          ])
          .optional(),
        valueInput: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNKNOWN_VALUE_INPUT',
                  'ENTITY_SELECTOR',
                  'USER_INPUT',
                  'STATIC_ITEMS',
                ])
                .optional(),
              label: z.string().describe('Input field label.').optional(),
              description: z
                .string()
                .describe('Input field description. May be used for a tooltip.')
                .optional()
                .nullable(),
              required: z
                .boolean()
                .describe(
                  'Whether the Wix user is required to enter a value in the field.\n\nDefault: `false`'
                )
                .optional(),
              reevaluateDynamicSchema: z
                .boolean()
                .describe(
                  'Whether Wix should reevaluate the dynamic schema when the field value changes.\n\nDefault: `false`'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
              }),
              z.object({
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
                entitySelector: z
                  .object({
                    _id: z.string().describe('Entity selector ID.').optional(),
                    queryFieldToFilterIdMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                      )
                      .optional(),
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryFieldToValueMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                      )
                      .optional(),
                  })
                  .describe('Entity selector details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                staticItems: z.never().optional(),
                userInput: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['UNKNOWN_USER_INPUT', 'NUMBER', 'BOOLEAN'])
                        .describe('User input type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z.never().optional(),
                      }),
                      z.object({
                        booleanInputOptions: z.never().optional(),
                        numberInputOptions: z
                          .object({
                            defaultValue: z
                              .number()
                              .int()
                              .describe('Default number value.')
                              .optional()
                              .nullable(),
                            minValue: z
                              .number()
                              .int()
                              .describe('Minimum number value.')
                              .optional()
                              .nullable(),
                            maxValue: z
                              .number()
                              .int()
                              .describe('Maximum number value.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Options for user input of type `number`.'),
                      }),
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z
                          .object({
                            defaultValue: z
                              .boolean()
                              .describe('Default boolean value.')
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for user input of type `boolean`.'
                          ),
                      }),
                    ])
                  )
                  .describe('User input details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z
                  .object({
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether the Wix user can select multiple items\n\nDefault: `false`.'
                      )
                      .optional(),
                    staticValues: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('item identifier')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                          value: z.string().describe('static value').optional(),
                          displayName: z
                            .string()
                            .describe('optional displayName for the value')
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Static value details.'),
              }),
            ])
          )
          .describe('Input field metadata.')
          .optional(),
        followUpFilters: z
          .array(z.lazy(() => wixAutomationsTriggercatalogV1FilterSchema))
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z.never().optional(),
        }),
        z.object({
          dynamicFieldKey: z.never().optional(),
          staticFieldKey: z.string().describe('The static field key.'),
        }),
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z
            .object({
              label: z.string().describe('Field key label.').optional(),
              description: z
                .string()
                .describe('Field key description.')
                .optional()
                .nullable(),
              entitySelector: z
                .object({
                  _id: z.string().describe('Entity selector ID.').optional(),
                  queryFieldToFilterIdMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                    )
                    .optional(),
                  multiSelect: z
                    .boolean()
                    .describe(
                      'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                    )
                    .optional(),
                  queryFieldToValueMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                    )
                    .optional(),
                })
                .describe(
                  'Selects the entity to be used as a dynamic field key.'
                )
                .optional(),
            })
            .describe('Dynamic field key data.'),
        }),
      ])
    );
  return z.object({
    _id: z
      .string()
      .describe('Trigger ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    options: z
      .object({
        trigger: z
          .object({
            _id: z
              .string()
              .describe('Trigger ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe(
                'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            triggerKey: z
              .string()
              .describe(
                'Trigger key.\n\nLearn about [setting up a trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).'
              )
              .min(1)
              .max(100),
            payloadDataSchema: z
              .record(z.string(), z.any())
              .describe(
                'Trigger [payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).'
              )
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe(
                'Trigger display name. Users see this name when they [choose a trigger for an automation](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger).'
              )
              .min(5)
              .max(80)
              .optional(),
            filters: z
              .array(wixAutomationsTriggercatalogV1FilterSchema)
              .max(5)
              .optional(),
            implementedMethods: z
              .object({
                validateConfiguration: z
                  .boolean()
                  .describe(
                    'Whether [Validate Configuration](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/validate-configuration) is implemented.\n\nDefault: `false`.'
                  )
                  .optional(),
                getDynamicSchema: z
                  .boolean()
                  .describe(
                    'Whether [Get Dynamic Schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/get-dynamic-schema) is implemented.\n\nDefault: `false`.'
                  )
                  .optional(),
                refreshPayload: z
                  .boolean()
                  .describe(
                    'Whether [Refresh Payload](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/refresh-payload) is implemented.\n\nDefault: `false`.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional [service plugin methods](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction#implement-the-service-plugin-methods) implemented for this trigger.'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the trigger was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the trigger was last updated.')
              .optional()
              .nullable(),
            baseUri: z
              .string()
              .describe(
                'Trigger base URI. Wix calls your trigger at this base address.\n\nLearn more about the [Wix Automations Trigger Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction).'
              )
              .max(256)
              .optional()
              .nullable(),
          })
          .describe('Trigger to update.')
          .optional(),
      })
      .optional(),
  });
})();
export const UpdateTriggerResponse = (() => {
  let wixAutomationsTriggercatalogV1FilterSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        _id: z
          .string()
          .describe('Filter ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        fieldKeyType: z
          .enum([
            'UNKNOWN_FIELD_KEY_TYPE',
            'STATIC_FIELD_KEY',
            'DYNAMIC_FIELD_KEY',
          ])
          .describe('Field key type.')
          .optional(),
        valueInput: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNKNOWN_VALUE_INPUT',
                  'ENTITY_SELECTOR',
                  'USER_INPUT',
                  'STATIC_ITEMS',
                ])
                .describe('Input value type.')
                .optional(),
              label: z.string().describe('Input field label.').optional(),
              description: z
                .string()
                .describe('Input field description. May be used for a tooltip.')
                .optional()
                .nullable(),
              required: z
                .boolean()
                .describe(
                  'Whether the Wix user is required to enter a value in the field.\n\nDefault: `false`'
                )
                .optional(),
              reevaluateDynamicSchema: z
                .boolean()
                .describe(
                  'Whether Wix should reevaluate the dynamic schema when the field value changes.\n\nDefault: `false`'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
              }),
              z.object({
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
                entitySelector: z
                  .object({
                    _id: z.string().describe('Entity selector ID.').optional(),
                    queryFieldToFilterIdMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                      )
                      .optional(),
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryFieldToValueMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                      )
                      .optional(),
                  })
                  .describe('Entity selector details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                staticItems: z.never().optional(),
                userInput: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['UNKNOWN_USER_INPUT', 'NUMBER', 'BOOLEAN'])
                        .describe('User input type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z.never().optional(),
                      }),
                      z.object({
                        booleanInputOptions: z.never().optional(),
                        numberInputOptions: z
                          .object({
                            defaultValue: z
                              .number()
                              .int()
                              .describe('Default number value.')
                              .optional()
                              .nullable(),
                            minValue: z
                              .number()
                              .int()
                              .describe('Minimum number value.')
                              .optional()
                              .nullable(),
                            maxValue: z
                              .number()
                              .int()
                              .describe('Maximum number value.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Options for user input of type `number`.'),
                      }),
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z
                          .object({
                            defaultValue: z
                              .boolean()
                              .describe('Default boolean value.')
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for user input of type `boolean`.'
                          ),
                      }),
                    ])
                  )
                  .describe('User input details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z
                  .object({
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether the Wix user can select multiple items\n\nDefault: `false`.'
                      )
                      .optional(),
                    staticValues: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('item identifier')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          value: z.string().describe('static value').optional(),
                          displayName: z
                            .string()
                            .describe('optional displayName for the value')
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Static value details.'),
              }),
            ])
          )
          .describe('Input field metadata.')
          .optional(),
        followUpFilters: z
          .array(z.lazy(() => wixAutomationsTriggercatalogV1FilterSchema))
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z.never().optional(),
        }),
        z.object({
          dynamicFieldKey: z.never().optional(),
          staticFieldKey: z.string().describe('The static field key.'),
        }),
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z
            .object({
              label: z.string().describe('Field key label.').optional(),
              description: z
                .string()
                .describe('Field key description.')
                .optional()
                .nullable(),
              entitySelector: z
                .object({
                  _id: z.string().describe('Entity selector ID.').optional(),
                  queryFieldToFilterIdMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                    )
                    .optional(),
                  multiSelect: z
                    .boolean()
                    .describe(
                      'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                    )
                    .optional(),
                  queryFieldToValueMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                    )
                    .optional(),
                })
                .describe(
                  'Selects the entity to be used as a dynamic field key.'
                )
                .optional(),
            })
            .describe('Dynamic field key data.'),
        }),
      ])
    );
  return z.object({
    _id: z
      .string()
      .describe('Trigger ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    appId: z
      .string()
      .describe(
        'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    triggerKey: z
      .string()
      .describe(
        'Trigger key.\n\nLearn about [setting up a trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).'
      )
      .min(1)
      .max(100)
      .optional(),
    payloadDataSchema: z
      .record(z.string(), z.any())
      .describe(
        'Trigger [payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).'
      )
      .optional()
      .nullable(),
    displayName: z
      .string()
      .describe(
        'Trigger display name. Users see this name when they [choose a trigger for an automation](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger).'
      )
      .min(5)
      .max(80)
      .optional(),
    filters: z
      .array(wixAutomationsTriggercatalogV1FilterSchema)
      .max(5)
      .optional(),
    implementedMethods: z
      .object({
        validateConfiguration: z
          .boolean()
          .describe(
            'Whether [Validate Configuration](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/validate-configuration) is implemented.\n\nDefault: `false`.'
          )
          .optional(),
        getDynamicSchema: z
          .boolean()
          .describe(
            'Whether [Get Dynamic Schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/get-dynamic-schema) is implemented.\n\nDefault: `false`.'
          )
          .optional(),
        refreshPayload: z
          .boolean()
          .describe(
            'Whether [Refresh Payload](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/refresh-payload) is implemented.\n\nDefault: `false`.'
          )
          .optional()
          .nullable(),
      })
      .describe(
        'Additional [service plugin methods](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction#implement-the-service-plugin-methods) implemented for this trigger.'
      )
      .optional(),
    _createdDate: z
      .date()
      .describe('Date and time the trigger was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the trigger was last updated.')
      .optional()
      .nullable(),
    baseUri: z
      .string()
      .describe(
        'Trigger base URI. Wix calls your trigger at this base address.\n\nLearn more about the [Wix Automations Trigger Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction).'
      )
      .max(256)
      .optional()
      .nullable(),
  });
})();
export const DeleteTriggerByAppIdAndKeyRequest = z.object({
  triggerAppId: z
    .string()
    .describe(
      'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    triggerKey: z
      .string()
      .describe(
        'Trigger key.\n\nLearn more about how trigger providers [add triggers to their app](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
      )
      .min(1)
      .max(100),
  }),
});
export const DeleteTriggerByAppIdAndKeyResponse = z.object({});
export const GetTriggerByAppIdAndKeyRequest = z.object({
  identifiers: z.object({
    appId: z
      .string()
      .describe(
        'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
      ),
    triggerKey: z
      .string()
      .describe(
        'Trigger key.\n\nLearn more about [adding a trigger to your app](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
      ),
  }),
  options: z
    .object({
      enrichWithIdentitySchema: z
        .boolean()
        .describe(
          'When the trigger payload schema includes the [`identityType` annotation](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema#identitytype), whether to include the full schema of that identity type. For example, when `true` and the `identityType` is `contact`, the full [contact schema](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/contact-object) is returned.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetTriggerByAppIdAndKeyResponse = (() => {
  let wixAutomationsTriggercatalogV1FilterSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        _id: z
          .string()
          .describe('Filter ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        fieldKeyType: z
          .enum([
            'UNKNOWN_FIELD_KEY_TYPE',
            'STATIC_FIELD_KEY',
            'DYNAMIC_FIELD_KEY',
          ])
          .describe('Field key type.')
          .optional(),
        valueInput: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNKNOWN_VALUE_INPUT',
                  'ENTITY_SELECTOR',
                  'USER_INPUT',
                  'STATIC_ITEMS',
                ])
                .describe('Input value type.')
                .optional(),
              label: z.string().describe('Input field label.').optional(),
              description: z
                .string()
                .describe('Input field description. May be used for a tooltip.')
                .optional()
                .nullable(),
              required: z
                .boolean()
                .describe(
                  'Whether the Wix user is required to enter a value in the field.\n\nDefault: `false`'
                )
                .optional(),
              reevaluateDynamicSchema: z
                .boolean()
                .describe(
                  'Whether Wix should reevaluate the dynamic schema when the field value changes.\n\nDefault: `false`'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
              }),
              z.object({
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
                entitySelector: z
                  .object({
                    _id: z.string().describe('Entity selector ID.').optional(),
                    queryFieldToFilterIdMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                      )
                      .optional(),
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryFieldToValueMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                      )
                      .optional(),
                  })
                  .describe('Entity selector details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                staticItems: z.never().optional(),
                userInput: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['UNKNOWN_USER_INPUT', 'NUMBER', 'BOOLEAN'])
                        .describe('User input type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z.never().optional(),
                      }),
                      z.object({
                        booleanInputOptions: z.never().optional(),
                        numberInputOptions: z
                          .object({
                            defaultValue: z
                              .number()
                              .int()
                              .describe('Default number value.')
                              .optional()
                              .nullable(),
                            minValue: z
                              .number()
                              .int()
                              .describe('Minimum number value.')
                              .optional()
                              .nullable(),
                            maxValue: z
                              .number()
                              .int()
                              .describe('Maximum number value.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Options for user input of type `number`.'),
                      }),
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z
                          .object({
                            defaultValue: z
                              .boolean()
                              .describe('Default boolean value.')
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for user input of type `boolean`.'
                          ),
                      }),
                    ])
                  )
                  .describe('User input details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z
                  .object({
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether the Wix user can select multiple items\n\nDefault: `false`.'
                      )
                      .optional(),
                    staticValues: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('item identifier')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          value: z.string().describe('static value').optional(),
                          displayName: z
                            .string()
                            .describe('optional displayName for the value')
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Static value details.'),
              }),
            ])
          )
          .describe('Input field metadata.')
          .optional(),
        followUpFilters: z
          .array(z.lazy(() => wixAutomationsTriggercatalogV1FilterSchema))
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z.never().optional(),
        }),
        z.object({
          dynamicFieldKey: z.never().optional(),
          staticFieldKey: z.string().describe('The static field key.'),
        }),
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z
            .object({
              label: z.string().describe('Field key label.').optional(),
              description: z
                .string()
                .describe('Field key description.')
                .optional()
                .nullable(),
              entitySelector: z
                .object({
                  _id: z.string().describe('Entity selector ID.').optional(),
                  queryFieldToFilterIdMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                    )
                    .optional(),
                  multiSelect: z
                    .boolean()
                    .describe(
                      'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                    )
                    .optional(),
                  queryFieldToValueMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                    )
                    .optional(),
                })
                .describe(
                  'Selects the entity to be used as a dynamic field key.'
                )
                .optional(),
            })
            .describe('Dynamic field key data.'),
        }),
      ])
    );
  return z.object({
    trigger: z
      .object({
        _id: z
          .string()
          .describe('Trigger ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appId: z
          .string()
          .describe(
            'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        triggerKey: z
          .string()
          .describe(
            'Trigger key.\n\nLearn about [setting up a trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).'
          )
          .min(1)
          .max(100)
          .optional(),
        payloadDataSchema: z
          .record(z.string(), z.any())
          .describe(
            'Trigger [payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).'
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            'Trigger display name. Users see this name when they [choose a trigger for an automation](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger).'
          )
          .min(5)
          .max(80)
          .optional(),
        filters: z
          .array(wixAutomationsTriggercatalogV1FilterSchema)
          .max(5)
          .optional(),
        implementedMethods: z
          .object({
            validateConfiguration: z
              .boolean()
              .describe(
                'Whether [Validate Configuration](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/validate-configuration) is implemented.\n\nDefault: `false`.'
              )
              .optional(),
            getDynamicSchema: z
              .boolean()
              .describe(
                'Whether [Get Dynamic Schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/get-dynamic-schema) is implemented.\n\nDefault: `false`.'
              )
              .optional(),
            refreshPayload: z
              .boolean()
              .describe(
                'Whether [Refresh Payload](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/refresh-payload) is implemented.\n\nDefault: `false`.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Additional [service plugin methods](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction#implement-the-service-plugin-methods) implemented for this trigger.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the trigger was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the trigger was last updated.')
          .optional()
          .nullable(),
        baseUri: z
          .string()
          .describe(
            'Trigger base URI. Wix calls your trigger at this base address.\n\nLearn more about the [Wix Automations Trigger Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction).'
          )
          .max(256)
          .optional()
          .nullable(),
      })
      .describe('The requested trigger.')
      .optional(),
  });
})();
export const QueryTriggersRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .optional(),
      }),
      z.xor([
        z.object({
          paging: z.never().optional(),
          cursorPaging: z.never().optional(),
        }),
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              'Paging options to limit and offset the number of items.'
            ),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe(
      'Query options.\n\nSee [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more information.'
    ),
});
export const QueryTriggersResponse = (() => {
  let wixAutomationsTriggercatalogV1FilterSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        _id: z
          .string()
          .describe('Filter ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        fieldKeyType: z
          .enum([
            'UNKNOWN_FIELD_KEY_TYPE',
            'STATIC_FIELD_KEY',
            'DYNAMIC_FIELD_KEY',
          ])
          .describe('Field key type.')
          .optional(),
        valueInput: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNKNOWN_VALUE_INPUT',
                  'ENTITY_SELECTOR',
                  'USER_INPUT',
                  'STATIC_ITEMS',
                ])
                .describe('Input value type.')
                .optional(),
              label: z.string().describe('Input field label.').optional(),
              description: z
                .string()
                .describe('Input field description. May be used for a tooltip.')
                .optional()
                .nullable(),
              required: z
                .boolean()
                .describe(
                  'Whether the Wix user is required to enter a value in the field.\n\nDefault: `false`'
                )
                .optional(),
              reevaluateDynamicSchema: z
                .boolean()
                .describe(
                  'Whether Wix should reevaluate the dynamic schema when the field value changes.\n\nDefault: `false`'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
              }),
              z.object({
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
                entitySelector: z
                  .object({
                    _id: z.string().describe('Entity selector ID.').optional(),
                    queryFieldToFilterIdMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                      )
                      .optional(),
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryFieldToValueMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                      )
                      .optional(),
                  })
                  .describe('Entity selector details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                staticItems: z.never().optional(),
                userInput: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['UNKNOWN_USER_INPUT', 'NUMBER', 'BOOLEAN'])
                        .describe('User input type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z.never().optional(),
                      }),
                      z.object({
                        booleanInputOptions: z.never().optional(),
                        numberInputOptions: z
                          .object({
                            defaultValue: z
                              .number()
                              .int()
                              .describe('Default number value.')
                              .optional()
                              .nullable(),
                            minValue: z
                              .number()
                              .int()
                              .describe('Minimum number value.')
                              .optional()
                              .nullable(),
                            maxValue: z
                              .number()
                              .int()
                              .describe('Maximum number value.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Options for user input of type `number`.'),
                      }),
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z
                          .object({
                            defaultValue: z
                              .boolean()
                              .describe('Default boolean value.')
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for user input of type `boolean`.'
                          ),
                      }),
                    ])
                  )
                  .describe('User input details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z
                  .object({
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether the Wix user can select multiple items\n\nDefault: `false`.'
                      )
                      .optional(),
                    staticValues: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('item identifier')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          value: z.string().describe('static value').optional(),
                          displayName: z
                            .string()
                            .describe('optional displayName for the value')
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Static value details.'),
              }),
            ])
          )
          .describe('Input field metadata.')
          .optional(),
        followUpFilters: z
          .array(z.lazy(() => wixAutomationsTriggercatalogV1FilterSchema))
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z.never().optional(),
        }),
        z.object({
          dynamicFieldKey: z.never().optional(),
          staticFieldKey: z.string().describe('The static field key.'),
        }),
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z
            .object({
              label: z.string().describe('Field key label.').optional(),
              description: z
                .string()
                .describe('Field key description.')
                .optional()
                .nullable(),
              entitySelector: z
                .object({
                  _id: z.string().describe('Entity selector ID.').optional(),
                  queryFieldToFilterIdMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                    )
                    .optional(),
                  multiSelect: z
                    .boolean()
                    .describe(
                      'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                    )
                    .optional(),
                  queryFieldToValueMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                    )
                    .optional(),
                })
                .describe(
                  'Selects the entity to be used as a dynamic field key.'
                )
                .optional(),
            })
            .describe('Dynamic field key data.'),
        }),
      ])
    );
  return z.object({
    results: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Trigger ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          appId: z
            .string()
            .describe(
              'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          triggerKey: z
            .string()
            .describe(
              'Trigger key.\n\nLearn about [setting up a trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).'
            )
            .min(1)
            .max(100)
            .optional(),
          payloadDataSchema: z
            .record(z.string(), z.any())
            .describe(
              'Trigger [payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).'
            )
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              'Trigger display name. Users see this name when they [choose a trigger for an automation](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger).'
            )
            .min(5)
            .max(80)
            .optional(),
          filters: z
            .array(wixAutomationsTriggercatalogV1FilterSchema)
            .max(5)
            .optional(),
          implementedMethods: z
            .object({
              validateConfiguration: z
                .boolean()
                .describe(
                  'Whether [Validate Configuration](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/validate-configuration) is implemented.\n\nDefault: `false`.'
                )
                .optional(),
              getDynamicSchema: z
                .boolean()
                .describe(
                  'Whether [Get Dynamic Schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/get-dynamic-schema) is implemented.\n\nDefault: `false`.'
                )
                .optional(),
              refreshPayload: z
                .boolean()
                .describe(
                  'Whether [Refresh Payload](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/refresh-payload) is implemented.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Additional [service plugin methods](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction#implement-the-service-plugin-methods) implemented for this trigger.'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the trigger was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the trigger was last updated.')
            .optional()
            .nullable(),
          baseUri: z
            .string()
            .describe(
              'Trigger base URI. Wix calls your trigger at this base address.\n\nLearn more about the [Wix Automations Trigger Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction).'
            )
            .max(256)
            .optional()
            .nullable(),
        })
      )
      .optional(),
    paging: z
      .object({
        count: z
          .number()
          .int()
          .describe('Number of items returned in the response.')
          .optional()
          .nullable(),
        offset: z
          .number()
          .int()
          .describe('Offset that was requested.')
          .optional()
          .nullable(),
        total: z
          .number()
          .int()
          .describe(
            'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
          )
          .optional()
          .nullable(),
        tooManyToCount: z
          .boolean()
          .describe(
            'Flag that indicates the server failed to calculate the `total` field.'
          )
          .optional()
          .nullable(),
        cursors: z
          .object({
            next: z
              .string()
              .describe(
                'Cursor string pointing to the next page in the list of results.'
              )
              .max(16000)
              .optional()
              .nullable(),
            prev: z
              .string()
              .describe(
                'Cursor pointing to the previous page in the list of results.'
              )
              .max(16000)
              .optional()
              .nullable(),
          })
          .describe(
            'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
          )
          .optional(),
      })
      .describe('Paging metadata.')
      .optional(),
  });
})();
export const ResolveTriggersRequest = z.object({
  options: z
    .object({
      query: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
              )
              .optional()
              .nullable(),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(512)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .optional(),
            fields: z.array(z.string()).optional(),
            fieldsets: z.array(z.string()).optional(),
          }),
          z.xor([
            z.object({
              paging: z.never().optional(),
              cursorPaging: z.never().optional(),
            }),
            z.object({
              cursorPaging: z.never().optional(),
              paging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe('Number of items to load.')
                    .min(0)
                    .optional()
                    .nullable(),
                  offset: z
                    .number()
                    .int()
                    .describe(
                      'Number of items to skip in the current sort order.'
                    )
                    .min(0)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Paging options to limit and offset the number of items.'
                ),
            }),
            z.object({
              paging: z.never().optional(),
              cursorPaging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items to return in the results.'
                    )
                    .min(0)
                    .max(100)
                    .optional()
                    .nullable(),
                  cursor: z
                    .string()
                    .describe(
                      "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
                ),
            }),
          ])
        )
        .describe('Query options.')
        .optional(),
      appId: z
        .string()
        .describe(
          'If provided, all triggers associated with the app ID are retrieved, regardless of maturity. The app must be installed on the site.'
        )
        .optional()
        .nullable(),
      basicFieldsOnly: z
        .boolean()
        .describe(
          'When `true`, the trigger payload schema, automation config schema, and filters are not returned.\nDefault: `false`.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ResolveTriggersResponse = (() => {
  let wixAutomationsTriggercatalogV1FilterSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        _id: z
          .string()
          .describe('Filter ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        fieldKeyType: z
          .enum([
            'UNKNOWN_FIELD_KEY_TYPE',
            'STATIC_FIELD_KEY',
            'DYNAMIC_FIELD_KEY',
          ])
          .describe('Field key type.')
          .optional(),
        valueInput: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNKNOWN_VALUE_INPUT',
                  'ENTITY_SELECTOR',
                  'USER_INPUT',
                  'STATIC_ITEMS',
                ])
                .describe('Input value type.')
                .optional(),
              label: z.string().describe('Input field label.').optional(),
              description: z
                .string()
                .describe('Input field description. May be used for a tooltip.')
                .optional()
                .nullable(),
              required: z
                .boolean()
                .describe(
                  'Whether the Wix user is required to enter a value in the field.\n\nDefault: `false`'
                )
                .optional(),
              reevaluateDynamicSchema: z
                .boolean()
                .describe(
                  'Whether Wix should reevaluate the dynamic schema when the field value changes.\n\nDefault: `false`'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
              }),
              z.object({
                userInput: z.never().optional(),
                staticItems: z.never().optional(),
                entitySelector: z
                  .object({
                    _id: z.string().describe('Entity selector ID.').optional(),
                    queryFieldToFilterIdMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                      )
                      .optional(),
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryFieldToValueMapping: z
                      .record(z.string(), z.string())
                      .describe(
                        "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                      )
                      .optional(),
                  })
                  .describe('Entity selector details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                staticItems: z.never().optional(),
                userInput: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['UNKNOWN_USER_INPUT', 'NUMBER', 'BOOLEAN'])
                        .describe('User input type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z.never().optional(),
                      }),
                      z.object({
                        booleanInputOptions: z.never().optional(),
                        numberInputOptions: z
                          .object({
                            defaultValue: z
                              .number()
                              .int()
                              .describe('Default number value.')
                              .optional()
                              .nullable(),
                            minValue: z
                              .number()
                              .int()
                              .describe('Minimum number value.')
                              .optional()
                              .nullable(),
                            maxValue: z
                              .number()
                              .int()
                              .describe('Maximum number value.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Options for user input of type `number`.'),
                      }),
                      z.object({
                        numberInputOptions: z.never().optional(),
                        booleanInputOptions: z
                          .object({
                            defaultValue: z
                              .boolean()
                              .describe('Default boolean value.')
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for user input of type `boolean`.'
                          ),
                      }),
                    ])
                  )
                  .describe('User input details.'),
              }),
              z.object({
                entitySelector: z.never().optional(),
                userInput: z.never().optional(),
                staticItems: z
                  .object({
                    multiSelect: z
                      .boolean()
                      .describe(
                        'Whether the Wix user can select multiple items\n\nDefault: `false`.'
                      )
                      .optional(),
                    staticValues: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('item identifier')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          value: z.string().describe('static value').optional(),
                          displayName: z
                            .string()
                            .describe('optional displayName for the value')
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Static value details.'),
              }),
            ])
          )
          .describe('Input field metadata.')
          .optional(),
        followUpFilters: z
          .array(z.lazy(() => wixAutomationsTriggercatalogV1FilterSchema))
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z.never().optional(),
        }),
        z.object({
          dynamicFieldKey: z.never().optional(),
          staticFieldKey: z.string().describe('The static field key.'),
        }),
        z.object({
          staticFieldKey: z.never().optional(),
          dynamicFieldKey: z
            .object({
              label: z.string().describe('Field key label.').optional(),
              description: z
                .string()
                .describe('Field key description.')
                .optional()
                .nullable(),
              entitySelector: z
                .object({
                  _id: z.string().describe('Entity selector ID.').optional(),
                  queryFieldToFilterIdMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to filter IDs.\n\nFor example, let's say the Workflows `Card moved` trigger has 2 filters:\n\n- Select a workflow, with filter ID `466d2067-05bd-44f9-99c7-d029b93e4feb`.\n- Select a step, with filter ID `b45f8475-bd44-4971-8820-7031ee12a708`.\n\nThe step entity selector requires the workflow ID as input in order to return the list of relevant steps.\nWe can map the `workflowId` field to the workflow filter in `queryFieldToFilterIdMapping`.\n\nWhen the Wix user selects a workflow in the workflow filter in the automation builder,\nWix passes the selected workflow's ID in the `workflowId` field to the step entity selector.\nThe step filter then shows the relevant steps of the selected workflow for the user to select."
                    )
                    .optional(),
                  multiSelect: z
                    .boolean()
                    .describe(
                      'Whether a Wix user can select multiple items.\n\nDefault: `false`'
                    )
                    .optional(),
                  queryFieldToValueMapping: z
                    .record(z.string(), z.string())
                    .describe(
                      "Mapping of the entity selector query fields to predefined values.\nThis is useful when using the same entity selector in different triggers.\n\nFor example: Let's say the `RSVPed to event` trigger has one filter, `Select a specific event`.\nThis filter has an ID `e91733fc-81b7-45ca-b50c-f0c292a23af5`.\n\nThe events entity selector has a query field of `eventType`, that should be passed in order to return the list of relevant events.\nOtherwise, all events will be returned regardless of type. Map the `eventType` field to `RSVP`.\n\nWhen the Wix user selects an event in the event filter in the automation builder,\nthe client shows only `RSVP` events in the selection options because the `eventType` field was set to `RSVP`."
                    )
                    .optional(),
                })
                .describe(
                  'Selects the entity to be used as a dynamic field key.'
                )
                .optional(),
            })
            .describe('Dynamic field key data.'),
        }),
      ])
    );
  return z.object({
    results: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Trigger ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          appId: z
            .string()
            .describe(
              'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          triggerKey: z
            .string()
            .describe(
              'Trigger key.\n\nLearn about [setting up a trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).'
            )
            .min(1)
            .max(100)
            .optional(),
          payloadDataSchema: z
            .record(z.string(), z.any())
            .describe(
              'Trigger [payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).'
            )
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              'Trigger display name. Users see this name when they [choose a trigger for an automation](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger).'
            )
            .min(5)
            .max(80)
            .optional(),
          filters: z
            .array(wixAutomationsTriggercatalogV1FilterSchema)
            .max(5)
            .optional(),
          implementedMethods: z
            .object({
              validateConfiguration: z
                .boolean()
                .describe(
                  'Whether [Validate Configuration](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/validate-configuration) is implemented.\n\nDefault: `false`.'
                )
                .optional(),
              getDynamicSchema: z
                .boolean()
                .describe(
                  'Whether [Get Dynamic Schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/get-dynamic-schema) is implemented.\n\nDefault: `false`.'
                )
                .optional(),
              refreshPayload: z
                .boolean()
                .describe(
                  'Whether [Refresh Payload](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/refresh-payload) is implemented.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Additional [service plugin methods](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction#implement-the-service-plugin-methods) implemented for this trigger.'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the trigger was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the trigger was last updated.')
            .optional()
            .nullable(),
          baseUri: z
            .string()
            .describe(
              'Trigger base URI. Wix calls your trigger at this base address.\n\nLearn more about the [Wix Automations Trigger Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/trigger-provider-service-plugin/introduction).'
            )
            .max(256)
            .optional()
            .nullable(),
        })
      )
      .optional(),
    paging: z
      .object({
        count: z
          .number()
          .int()
          .describe('Number of items returned in the response.')
          .optional()
          .nullable(),
        offset: z
          .number()
          .int()
          .describe('Offset that was requested.')
          .optional()
          .nullable(),
        total: z
          .number()
          .int()
          .describe(
            'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
          )
          .optional()
          .nullable(),
        tooManyToCount: z
          .boolean()
          .describe(
            'Flag that indicates the server failed to calculate the `total` field.'
          )
          .optional()
          .nullable(),
        cursors: z
          .object({
            next: z
              .string()
              .describe(
                'Cursor string pointing to the next page in the list of results.'
              )
              .max(16000)
              .optional()
              .nullable(),
            prev: z
              .string()
              .describe(
                'Cursor pointing to the previous page in the list of results.'
              )
              .max(16000)
              .optional()
              .nullable(),
          })
          .describe(
            'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
          )
          .optional(),
      })
      .describe('Paging metadata of the response.')
      .optional(),
  });
})();
export const GetTriggerDynamicSchemaRequest = z.object({
  appId: z
    .string()
    .describe(
      'ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    triggerKey: z
      .string()
      .describe(
        'Trigger key.\n\nLearn more about [adding a trigger to your app](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).'
      )
      .min(1)
      .max(100),
    selectedFilterOptions: z
      .array(
        z.object({
          fieldKey: z
            .string()
            .describe(
              "Key representing a field from the trigger's [payload schema](https://dev.wix.com/docs/rest/business-management/automations/triggers/the-trigger-payload-schema) that has been configured as an [item selection filter](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/triggers/filter-fields#item-selection). For example `formId`."
            )
            .min(1)
            .max(110)
            .optional(),
          values: z.array(z.string()).max(100).optional(),
        })
      )
      .max(5)
      .optional(),
  }),
});
export const GetTriggerDynamicSchemaResponse = z.object({
  dynamicSchema: z
    .record(z.string(), z.any())
    .describe(
      'A [JSON schema](https://json-schema.org/) corresponding to the filter selection sent in the request.'
    )
    .optional()
    .nullable(),
});
export const GetIdentitiesSchemaRequest = z.object({});
export const GetIdentitiesSchemaResponse = z.object({
  identitiesSchema: z
    .record(z.string(), z.any())
    .describe(
      'identities schema with a property per identity (currently only "contact"):\nthe schema would look like this:\n{\n"type": "object",\n"properties": {\n"contact": {\n"type": "object",\n"properties": {\n...contact identity fields\n}\n}\n}\n}'
    )
    .optional()
    .nullable(),
});
